import { useMemo } from "react";
import {
  Activity as ActivityType,
  ActivityData,
  Notes,
  NewActivity
} from "@/entities/activities/types";
import { getFullName } from "@/entities/people/helpers";
import { getEnumTKey } from "@/utils/strings";
import { usePeople } from "@/entities/people/hooks";
import { useTranslation } from "react-i18next";
import { activityTypeToNewActivityType } from "@/components/layouts/Activities/components/Activity";
import {
  formatTimeRange,
  isUTCMidnight,
  lowerCaseTime,
  simpleTime
} from "@/utils/datetime";
import { TFunction } from "i18next";
import { filter, map, take, uniq } from "lodash";

export const useActivityTime = (notes: Notes | null) =>
  useMemo(() => {
    if (!notes?.date) return null;

    if (notes?.startTime && notes?.endTime) {
      return formatTimeRange(
        simpleTime(new Date(`${notes.startTime}`)),
        simpleTime(new Date(`${notes.endTime}`)),
        true
      );
    }

    if (isUTCMidnight(notes.date)) {
      return null;
    }

    return lowerCaseTime(simpleTime(new Date(notes.date)));
  }, [notes?.date, notes?.startTime, notes?.endTime]);

export const useActivityPeople = (
  peopleIds: string[] = [],
  notes: Notes | null,
  showAll: boolean = false
) => {
  const safePeopleIds = Array.isArray(peopleIds) ? peopleIds : [];
  const hasParticipants =
    Array.isArray(notes?.participants) && notes.participants.length > 0;
  const hasResolvedParticipantNames =
    hasParticipants &&
    (notes?.participants || []).some((participant) =>
      Boolean(participant?.name?.trim())
    );

  // Skip the API call only when enriched participants have resolved names
  const activityPeople = usePeople(
    hasResolvedParticipantNames ? [] : safePeopleIds
  ).entities;

  return useMemo(() => {
    // Prefer enriched participant names from the fetch endpoint
    if (hasParticipants && notes?.participants) {
      const participantNames = filter(
        map(notes.participants, (participant) => participant?.name?.trim()),
        Boolean
      ) as string[];

      const uniqueNames = uniq(participantNames);

      if (uniqueNames.length > 0) {
        const first2Names = take(uniqueNames, 2);
        const extraCount = Math.max(0, uniqueNames.length - 2);

        return showAll
          ? uniqueNames.join(", ")
          : `${first2Names.join(", ")}${extraCount ? ` +${extraCount}` : ""}`;
      }
      // Fall through to peopleIds/attendees logic when names are empty
    }

    const hasPeopleIds =
      Array.isArray(safePeopleIds) && safePeopleIds?.length > 0;
    const hasAttendees =
      Array.isArray(notes?.attendees) && notes?.attendees?.length > 0;

    if (!hasPeopleIds && !hasAttendees) return "";

    const peopleFromIds = map(
      filter(
        map(safePeopleIds || [], (id) =>
          activityPeople.find((p) => p?.id === id)
        ),
        Boolean
      ),
      getFullName
    );

    const attendeeNames = filter(
      map(notes?.attendees || [], (a) => a?.name),
      Boolean
    );

    const allPeople = [...peopleFromIds, ...attendeeNames];
    const allNames = uniq(
      map(allPeople, (name) => (typeof name === "string" ? name : String(name)))
    );
    const first2Names = take(allNames, 2);
    const extraCount = Math.max(0, allNames.length - 2);

    return showAll
      ? allNames.join(", ")
      : `${first2Names.join(", ")}${extraCount ? ` +${extraCount}` : ""}`;
  }, [
    safePeopleIds,
    activityPeople,
    notes?.attendees,
    notes?.participants,
    hasParticipants,
    showAll
  ]);
};

const useActivityTitleInternal = (
  type: ActivityType | undefined,
  notes: Notes | null,
  people: string
) => {
  const { t } = useTranslation();

  return useMemo(() => {
    switch (type) {
      case ActivityType.QUOTE_SENT:
        return t("activities.quoteSent");

      case ActivityType.NDA_SENT:
      case ActivityType.NDA_SIGNED:
        return t(
          getEnumTKey("activities", "ndaTypes", ActivityType, Number(type))
        );

      case ActivityType.TEXT:
        const messageType = notes?.messageType
          ? t(`activities.messageTypes.${notes.messageType}`, {
              defaultValue: t(
                getEnumTKey(
                  "activities",
                  "newActivities",
                  NewActivity,
                  NewActivity.TEXT
                )
              )
            })
          : "";
        return `${messageType} ${t("activities.messageWith")} ${people}`;

      default:
        return notes?.subject || "";
    }
  }, [type, notes?.messageType, notes?.subject, people, t]);
};

export const useActivityType = (
  t: TFunction<"translation", undefined>,
  activity: ActivityData | undefined
) => {
  if (!activity) return "";
  const { type, notes } = activity;
  if (type === ActivityType.ONLINE_MEETING) {
    return `${t(getEnumTKey("activities", "meetingTypes", ActivityType, Number(type)))} ${t("activities.meeting")}`;
  }
  const typeLabel = t(
    getEnumTKey(
      "activities",
      "newActivities",
      NewActivity,
      activityTypeToNewActivityType[type as ActivityType] as NewActivity
    )
  );
  if (type === ActivityType.TEXT && notes?.messageType) {
    return t(`activities.messageTypes.${notes.messageType}`, {
      defaultValue: typeLabel
    });
  }
  return typeLabel;
};

export const useActivityTitle = (activity: ActivityData | undefined) => {
  const type = activity?.type;
  const notes = activity?.notes || null;
  const peopleIds = activity?.peopleIds;

  const safePeopleIds = Array.isArray(peopleIds) ? peopleIds : [];

  const people = useActivityPeople(safePeopleIds, notes);
  const title = useActivityTitleInternal(type, notes, people);

  return activity ? title : "";
};
